import { type SoftwareSubscription, type Tenant } from 'src/types/azure'

import api from 'src/constants/api'

import useAccount from 'src/hooks/utils/useAccount'
import useQuery from 'src/hooks/utils/useQuery'

import moment from 'moment'

interface useSoftwareSubscriptionsQueryProps {
  tenants: Tenant[]
}

const useSoftwareSubscriptionsQuery = ({ tenants }: useSoftwareSubscriptionsQueryProps) => {
  const account = useAccount()

  const { data: softwareSubscriptions, ...rest } = useQuery(
    ['software-subscriptions', account.id, tenants],
    async ({ signal }) => {
      const response = await api.post(
        `/servicenow/account/${account.id}/software_subscriptions`,
        {
          tenant_ids: tenants.map((t) => t.id),
          start_date: '1970-01-01',
          end_date: moment().format('YYYY-MM-DD')
        },
        { signal }
      )
      return response.data as SoftwareSubscription[]
    },
    {
      enabled: !!account.id && !!tenants && !!tenants.length
    }
  )

  const instances =
    softwareSubscriptions?.map((subscription) => ({
      ...subscription,
      type: 'Software Subscription',
      expires: subscription.term === 'P1Y' && subscription.charges?.[0]?.date ? moment(subscription.charges?.[0]?.date).add(1, 'year') : 'N/A'
    })) || []

  const stateExtendedInstances = instances.map((instance) => {
    const expireDate = instance.term === 'P1Y' && instance.charges?.[0]?.date ? moment(instance.charges?.[0]?.date).add(1, 'year') : null

    const lastChargeCancelled = instance.charges?.[0]?.type === 'cancelImmediate'
    const isExpired = expireDate && moment(expireDate).isBefore(moment())

    const state = isExpired ? 'Expired' : lastChargeCancelled ? 'Cancelled' : 'Active'

    return {
      ...instance,
      state,
      expires: expireDate ?? 'N/A'
    }
  })

  return { data: stateExtendedInstances, ...rest }
}

export default useSoftwareSubscriptionsQuery
